import React, { useState, useEffect } from 'react';
import { Dumbbell, Plus, Edit2, Trash2, Users, MessageSquare, LogOut, X, RefreshCw, BookOpen, Play, Image, CreditCard } from 'lucide-react';
import { 
  getCoachTemplates, 
  createWorkoutTemplate, 
  updateWorkoutTemplate, 
  deleteWorkoutTemplate,
  getCoachAthletes,
  getRecentFeedback,
  getAthleteWeekSchedule,
  saveWeeklySchedule
} from './workout-api';
import {
  getCoachExercises,
  createExercise,
  updateExercise,
  deleteExercise
} from './exercise-library-api';
import { logout } from './auth';
import WelcomeModal from './WelcomeModal';
import InteractiveTutorial from './InteractiveTutorial';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase-config';

const CoachDashboard = ({ user, onLogout }) => {
  const [templates, setTemplates] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [athleteSchedule, setAthleteSchedule] = useState(null);
  const [feedbackDateFilter, setFeedbackDateFilter] = useState('all');
  const [isAddingExercise, setIsAddingExercise] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    category: 'strength',
    description: '',
    videoUrl: '',
    imageUrl: '',
    tips: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ intensity: 'recovery', name: '', prescription: '' });
  const [scheduleWeek, setScheduleWeek] = useState(new Date());
  
  // Onboarding states
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Exercise viewer state
  const [viewingExercise, setViewingExercise] = useState(null);
  const [freeWriteDays, setFreeWriteDays] = useState({});

  const intensityOptions = [
    { value: 'recovery', label: 'Recovery', color: '#6b7280' },
    { value: 'easy', label: 'Easy', color: '#22c55e' },
    { value: 'tempo', label: 'Tempo', color: '#f59e0b' },
    { value: 'threshold', label: 'Threshold', color: '#f97316' },
    { value: 'hard', label: 'Hard', color: '#2563eb' },
    { value: 'max', label: 'Max Effort', color: '#dc2626' }
  ];

  const categories = ['strength', 'cardio', 'flexibility', 'balance', 'sport-specific'];

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem(`welcome_seen_${user?.uid}`);
    if (user && !hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    const [templatesData, athletesData, feedbackData, exercisesData] = await Promise.all([
      getCoachTemplates(user.uid),
      getCoachAthletes(user.uid),
      getRecentFeedback(user.uid, 10),
      getCoachExercises(user.uid)
    ]);
    
    setTemplates(templatesData);
    setAthletes(athletesData);
    setRecentFeedback(feedbackData);
    setExercises(exercisesData);
  };

    const handleRemoveAthlete = async (athleteId) => {
  if (!window.confirm('Remove this athlete from your roster?')) return;
  
    try {
    await updateDoc(doc(db, 'users', athleteId), {
      coach_id: null,
      active: false
    });
    alert('✅ Athlete removed.');
    loadData();
  } catch (error) {
    alert('❌ Error removing athlete: ' + error.message);
  }
};
  const handleWelcomeClose = () => {
    localStorage.setItem(`welcome_seen_${user?.uid}`, 'true');
    setShowWelcome(false);
  };

  const handleStartTour = () => {
    localStorage.setItem(`welcome_seen_${user?.uid}`, 'true');
    setShowWelcome(false);
    setShowTutorial(true);
  };

  const handleTutorialComplete = () => {
    localStorage.setItem(`tutorial_completed_${user?.uid}`, 'true');
    setShowTutorial(false);
  };

  // Template functions
  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.prescription.trim()) {
      alert('⚠️ Name and prescription are required!');
      return;
    }

    const result = editingTemplate
      ? await updateWorkoutTemplate(editingTemplate.id, formData)
      : await createWorkoutTemplate(user.uid, formData);

    if (result.success) {
      alert(editingTemplate ? '✅ Template updated!' : '✅ Template created!');
      setIsEditing(false);
      setEditingTemplate(null);
      setFormData({ intensity: 'recovery', name: '', prescription: '' });
      loadData();
    } else {
      alert('❌ Error: ' + result.error);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      intensity: template.intensity,
      name: template.name,
      prescription: template.prescription
    });
    setIsEditing(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;

    const result = await deleteWorkoutTemplate(templateId);
    if (result.success) {
      alert('✅ Template deleted!');
      loadData();
    } else {
      alert('❌ Error: ' + result.error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingTemplate(null);
    setFormData({ intensity: 'recovery', name: '', prescription: '' });
  };

  // Exercise Library functions
  const handleCreateExercise = async () => {
    if (!exerciseForm.name.trim()) {
      alert('⚠️ Exercise name is required!');
      return;
    }

    const result = await createExercise(user.uid, exerciseForm);
    if (result.success) {
      alert('✅ Exercise created!');
      setIsAddingExercise(false);
      setExerciseForm({
        name: '',
        category: 'strength',
        description: '',
        videoUrl: '',
        imageUrl: '',
        tips: ''
      });
      loadData();
    } else {
      alert('❌ Error: ' + result.error);
    }
  };

  const handleUpdateExercise = async () => {
    const result = await updateExercise(editingExercise.id, exerciseForm);
    if (result.success) {
      alert('✅ Exercise updated!');
      setEditingExercise(null);
      setExerciseForm({
        name: '',
        category: 'strength',
        description: '',
        videoUrl: '',
        imageUrl: '',
        tips: ''
      });
      loadData();
    } else {
      alert('❌ Error: ' + result.error);
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    if (!window.confirm('Delete this exercise?')) return;

    const result = await deleteExercise(exerciseId);
    if (result.success) {
      alert('✅ Exercise deleted!');
      loadData();
    } else {
      alert('❌ Error: ' + result.error);
    }
  };

  const startEditingExercise = (exercise) => {
    setEditingExercise(exercise);
    setExerciseForm({
      name: exercise.name,
      category: exercise.category,
      description: exercise.description,
      videoUrl: exercise.videoUrl || '',
      imageUrl: exercise.imageUrl || '',
      tips: exercise.tips || ''
    });
  };

  const cancelExerciseEdit = () => {
    setEditingExercise(null);
    setIsAddingExercise(false);
    setExerciseForm({
      name: '',
      category: 'strength',
      description: '',
      videoUrl: '',
      imageUrl: '',
      tips: ''
    });
  };

 const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      onLogout();
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch('https://coach-log-api.vercel.app/api/create-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          returnUrl: window.location.origin,
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Could not open subscription management. Please try again.');
      }
    } catch (error) {
      console.error('Portal error:', error);
      alert('Something went wrong. Please try again.');
    }
  };
  const getIntensityColor = (intensity) => {
    return intensityOptions.find(opt => opt.value === intensity)?.color || '#6b7280';
  };

  const loadAthleteSchedule = async (athlete, weekDate = new Date()) => {
   setSelectedAthlete(athlete);
   const weekStart = new Date(weekDate);
   weekStart.setDate(weekStart.getDate() - weekStart.getDay());
   const weekEnd = new Date(weekStart);
   weekEnd.setDate(weekEnd.getDate() + 6);
   const schedule = await getAthleteWeekSchedule(athlete.id, weekStart, weekEnd);
   setAthleteSchedule(schedule);
  };

  const navigateScheduleWeek = (direction) => {
   const newWeek = new Date(scheduleWeek);
   newWeek.setDate(newWeek.getDate() + (direction * 7));
   setScheduleWeek(newWeek);
   loadAthleteSchedule(selectedAthlete, newWeek);
 };

  const generateNewCoachCode = async () => {
    const { generateCoachCode } = await import('./auth');
    try {
      const newCode = await generateCoachCode(user.uid);
      if (newCode) {
        window.location.reload();
      }
    } catch (error) {
      alert('❌ Error generating coach code: ' + error.message);
    }
  };

  const getFilteredFeedback = () => {
    let filtered = recentFeedback;

    if (selectedAthlete) {
      filtered = filtered.filter(feedback => feedback.athlete_id === selectedAthlete.id);
    }

    if (feedbackDateFilter !== 'all') {
      const now = new Date();
      const filterDays = {
        'week': 7,
        'twoweeks': 14,
        'month': 30
      };
      const daysBack = filterDays[feedbackDateFilter];
      const filterDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));

      filtered = filtered.filter(feedback => {
        const feedbackDate = feedback.completed_at.toDate();
        return feedbackDate >= filterDate;
      });
    }

    return filtered.sort((a, b) => b.completed_at.toDate() - a.completed_at.toDate());
  };

  const handleSaveSchedule = async () => {
    if (!selectedAthlete || !athleteSchedule) return;

    const result = await saveWeeklySchedule(athleteSchedule);
    if (result.success) {
      alert('✅ Schedule saved successfully!');
      setSelectedAthlete(null);
      setAthleteSchedule(null);
    } else {
      alert('❌ Error saving schedule: ' + result.error);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
      padding: '24px',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '24px',
          border: '1px solid rgba(37, 99, 235, 0.1)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img 
                src="/coach-logo.png" 
                alt="Coach's Log" 
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  boxShadow: '0 8px 25px rgba(37, 99, 235, 0.15)'
                }}
              />
              <div>
                <h1 style={{
                  fontSize: '32px',
                  fontWeight: '800',
                  color: '#1e293b',
                  margin: 0,
                  fontFamily: '"Inter", sans-serif',
                  letterSpacing: '-0.02em'
                }}>
                  <span style={{ color: '#2563eb' }}>Coach's</span> <span style={{ color: '#1e293b' }}>Log</span>
                </h1>
                <p style={{
                  color: '#64748b',
                  margin: '4px 0 0 0',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  Training Management Platform • Welcome, {user?.name || user?.email}
                </p>

                <div style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  background: 'rgba(37, 99, 235, 0.05)',
                  border: '1px solid rgba(37, 99, 235, 0.2)',
                  borderRadius: '12px',
                  display: 'inline-block'
                }}>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#2563eb',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: '4px'
                  }}>
                    👤 Your Coach Code:
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '800',
                    color: user?.coach_code ? '#1e293b' : '#64748b',
                    fontFamily: '"JetBrains Mono", monospace',
                    letterSpacing: '0.1em'
                  }}>
                    {user?.coach_code || 'NOT SET'}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: '#64748b',
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    {user?.coach_code ? 'Share this with your athletes' : 'Generate your code to get started'}
                  </div>
                </div>

                {user?.coach_code ? (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(user.coach_code);
                      alert('✅ Coach code copied to clipboard!');
                    }}
                    style={{
                      marginLeft: '12px',
                      padding: '10px 16px',
                      background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '12px',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s',
                      letterSpacing: '0.05em'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    📋 Copy
                  </button>
                ) : (
                  <button
                    onClick={generateNewCoachCode}
                    style={{
                      marginTop: '8px',
                      padding: '10px 16px',
                      background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '12px',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      transition: 'all 0.2s',
                      letterSpacing: '0.05em'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    ⚡ Generate Code
                  </button>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => setShowHelp(!showHelp)}
                style={{
                  padding: '12px',
                  background: '#f8fafc',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.color = '#2563eb';
                  e.target.style.background = '#f0f9ff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.color = '#64748b';
                  e.target.style.background = '#f8fafc';
                }}
              >
                ❓
                
                {showHelp && (
                  <div style={{
                    position: 'absolute',
                    top: '110%',
                    right: 0,
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '8px',
                    minWidth: '200px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    zIndex: 1000
                  }}
                  onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => {
                        setShowTutorial(true);
                        setShowHelp(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'transparent',
                        border: 'none',
                        color: '#374151',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      🎓 Take Tutorial
                    </button>
                    
                    <a
                      href="mailto:tim@foundationalsports.com"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '12px',
                        background: 'transparent',
                        color: '#374151',
                        textAlign: 'left',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      📧 Contact Support
                    </a>
                  </div>
                )}
              </button>

              {user?.subscription_status && (
        <button
          onClick={handleManageSubscription}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#f8fafc',
            color: '#64748b',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            transition: 'all 0.2s',
            marginRight: '12px'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#2563eb';
            e.target.style.color = '#2563eb';
            e.target.style.background = '#eff6ff';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.color = '#64748b';
            e.target.style.background = '#f8fafc';
          }}
        >
          <CreditCard size={20} />
          Manage Subscription
        </button>
        )}

        <button
          onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: '#f8fafc',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#dc2626';
                  e.target.style.color = '#dc2626';
                  e.target.style.background = '#fef2f2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.color = '#64748b';
                  e.target.style.background = '#f8fafc';
                }}
              >
                <LogOut size={20} />
                Logout
              </button>
            </div>
          </div>

          {/* Clean Navigation Tabs - No Counts or Restrictions */}
          <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #e2e8f0', marginBottom: '0' }}>
            {[
              { id: 'templates', label: 'Workout Templates', icon: Dumbbell },
              { id: 'library', label: 'Exercise Library', icon: BookOpen },
              { id: 'athletes', label: 'Athletes', icon: Users },
              { id: 'feedback', label: 'Recent Feedback', icon: MessageSquare }
            ].filter(tab => (user?.plan || 'coach') !== 'athlete' || (tab.id !== 'athletes' && tab.id !== 'feedback')).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px 24px',
                  background: activeTab === tab.id 
                    ? 'linear-gradient(135deg, #2563eb, #3b82f6)'
                    : 'transparent',
                  color: activeTab === tab.id ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '12px 12px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  transition: 'all 0.2s',
                  marginBottom: activeTab === tab.id ? '1px' : '0'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'rgba(37, 99, 235, 0.05)';
                    e.target.style.color = '#2563eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#64748b';
                  }
                }}
              >
                <tab.icon size={20} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '40px',
          border: '1px solid rgba(37, 99, 235, 0.1)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          minHeight: '60vh'
        }}>
          
          {/* Workout Templates Tab */}
          {activeTab === 'templates' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Dumbbell style={{ color: '#2563eb' }} size={32} />
                  Workout Templates
                </h2>
                
                <button
                  onClick={() => setIsEditing(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <Plus size={20} />
                  New Template
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '24px'
              }}>
                {templates.map(template => (
                  <div
                    key={template.id}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      padding: '24px',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.3s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                      <div style={{
                        padding: '6px 12px',
                        background: getIntensityColor(template.intensity),
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {intensityOptions.find(opt => opt.value === template.intensity)?.label}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(template);
                          }}
                          style={{
                            padding: '8px',
                            background: '#f0f9ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '8px',
                            color: '#2563eb',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#dbeafe';
                            e.target.style.borderColor = '#60a5fa';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#f0f9ff';
                            e.target.style.borderColor = '#bfdbfe';
                          }}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(template.id);
                          }}
                          style={{
                            padding: '8px',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            color: '#dc2626',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fee2e2';
                            e.target.style.borderColor = '#f87171';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fef2f2';
                            e.target.style.borderColor = '#fecaca';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#1e293b',
                      margin: '0 0 12px 0'
                    }}>
                      {template.name}
                    </h3>
                    
                    <p style={{
                      color: '#64748b',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      margin: 0,
                      fontWeight: '500'
                    }}>
                      {template.prescription}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Athletes Tab */}
          {activeTab === 'athletes' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Users style={{ color: '#2563eb' }} size={32} />
                  Your Athletes
                </h2>
              </div>

              {athletes.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  {athletes.map(athlete => (
                    <div
                      key={athlete.id}
                      style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#1e293b',
                          margin: 0
                        }}>
                          {athlete.name || athlete.email}
                        </h3>
                      </div>
                      
                      <p style={{
                        fontSize: '14px',
                        color: '#64748b',
                        margin: '0 0 16px 0',
                        fontWeight: '500'
                      }}>
                        {athlete.email}
                      </p>
                      
                      <button
                        onClick={() => loadAthleteSchedule(athlete)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                          border: 'none',
                          borderRadius: '10px',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        >
  Manage Schedule
</button>

<button
  onClick={() => handleRemoveAthlete(athlete.id)}
  style={{
    width: '100%',
    marginTop: '8px',
    padding: '12px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '10px',
    color: '#dc2626',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    transition: 'all 0.2s'
  }}
>
  Remove Athlete
</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '80px 20px',
                  color: '#94a3b8'
                }}>
                  <Users size={64} style={{ margin: '0 auto 16px', color: '#cbd5e1' }} />
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#475569',
                    marginBottom: '8px'
                  }}>
                    No Athletes Yet
                  </h3>
                  <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '20px' }}>
                    Share your coach code with athletes to get started!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Exercise Library Tab */}
          {activeTab === 'library' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <BookOpen style={{ color: '#2563eb' }} size={32} />
                  Exercise Library
                </h2>
                
                <button
                  onClick={() => setIsAddingExercise(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <Plus size={20} />
                  Add Exercise
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '20px'
              }}>
                {exercises.map(exercise => (
                  <div
                    key={exercise.id}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      padding: '20px',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.3s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => setViewingExercise(exercise)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                      <div style={{
                        padding: '4px 8px',
                        background: '#f1f5f9',
                        color: '#64748b',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {exercise.category}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingExercise(exercise);
                          }}
                          style={{
                            padding: '6px',
                            background: '#f0f9ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '6px',
                            color: '#2563eb',
                            cursor: 'pointer'
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteExercise(exercise.id);
                          }}
                          style={{
                            padding: '6px',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            color: '#dc2626',
                            cursor: 'pointer'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1e293b',
                      margin: '0 0 8px 0'
                    }}>
                      {exercise.name}
                    </h3>
                    
                    <p style={{
                      color: '#64748b',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      margin: 0,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {exercise.description || 'No description provided'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Feedback Tab */}
          {activeTab === 'feedback' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <MessageSquare style={{ color: '#2563eb' }} size={32} />
                  Recent Feedback
                </h2>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <select
                    value={feedbackDateFilter}
                    onChange={(e) => setFeedbackDateFilter(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All Time</option>
                    <option value="week">Past Week</option>
                    <option value="twoweeks">Past 2 Weeks</option>
                    <option value="month">Past Month</option>
                  </select>
                  
                  <button
                    onClick={loadData}
                    style={{
                      padding: '10px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#64748b',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.color = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.color = '#64748b';
                    }}
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
              </div>

              {getFilteredFeedback().length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '20px'
                }}>
                  {getFilteredFeedback().map(feedback => (
                    <div
                      key={feedback.id}
                      style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        border: feedback.is_personal_best ? '1px solid #f59e0b' : '1px solid #e2e8f0',
                        position: 'relative',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {feedback.is_personal_best && (
                        <div style={{
                          position: 'absolute',
                          top: '16px',
                          right: '90px',
                          color: '#f59e0b',
                          fontSize: '20px'
                        }}>
                          ⭐
                        </div>
                      )}
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        marginBottom: '12px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#1e293b',
                          margin: 0
                        }}>
                          {feedback.athlete_name}
                        </h3>
                        
                        <div style={{
                          fontSize: '12px',
                          color: '#64748b',
                          fontWeight: '600'
                        }}>
                          {feedback.completed_at?.toDate().toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#2563eb',
                        marginBottom: '8px'
                      }}>
                        {feedback.workout_name}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          color: '#64748b',
                          fontWeight: '600'
                        }}>
                          Effort: {feedback.effort_level}/10
                        </div>
                        
                        <div style={{
                          padding: '4px 8px',
                          background: '#f1f5f9',
                          borderRadius: '12px',
                          fontSize: '12px',
                          color: '#64748b',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {feedback.workout_day}
                        </div>
                      </div>
                      
                      {feedback.notes && (
                        <p style={{
                          fontSize: '14px',
                          color: '#1e293b',
                          fontStyle: 'italic',
                          margin: 0,
                          lineHeight: '1.5'
                        }}>
                          "{feedback.notes}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '80px 20px',
                  color: '#94a3b8'
                }}>
                  <MessageSquare size={64} style={{ margin: '0 auto 16px', color: '#cbd5e1' }} />
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#475569',
                    marginBottom: '8px'
                  }}>
                    No Feedback Yet
                  </h3>
                  <p style={{ fontSize: '16px', fontWeight: '500' }}>
                    Athletes haven't completed any workouts yet.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Schedule Modal */}
        {selectedAthlete && athleteSchedule && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px'
          }} onClick={() => { setSelectedAthlete(null); setAthleteSchedule(null); }}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '1200px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }} onClick={(e) => e.stopPropagation()}>
              
         <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '32px'
              }}>
                <div>
                    <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
                      Schedule for {selectedAthlete.name}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                      <button
                        onClick={() => navigateScheduleWeek(-1)}
                        style={{ padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                      >
                        ← Prev Week
                      </button>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>
                        {(() => {
                          const start = new Date(scheduleWeek);
                          start.setDate(start.getDate() - start.getDay());
                          return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                        })()}
                      </span>
                      <button
                        onClick={() => navigateScheduleWeek(1)}
                        style={{ padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                      >
                        Next Week →
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedAthlete(null); setAthleteSchedule(null); }}
                    style={{ padding: '8px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#64748b', cursor: 'pointer' }}
                  >
                    <X size={20} />
                  </button>
                </div>

              {/* Week Days */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '16px',
                marginBottom: '24px'
              }}>
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
                  // Calculate the date for this day
                  const dayDate = new Date(athleteSchedule.week_start);
                  dayDate.setDate(dayDate.getDate() + index);
                  
                  return (
                    <div
                      key={day}
                      style={{
                        background: '#f8fafc',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '1px solid #e2e8f0',
                        minHeight: '120px'
                      }}
                    >
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: '#1e293b',
                        marginBottom: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {day}
                      </h4>
                      <p style={{
                        fontSize: '12px',
                        color: '#64748b',
                        margin: '0 0 12px 0',
                        fontWeight: '500'
                      }}>
                        {dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                      
                      {/* Free-write toggle */}
                <div
                  onClick={() => setFreeWriteDays(prev => ({ ...prev, [index]: !prev[index] }))}
                  style={{
                    fontSize: '11px',
                    color: '#2563eb',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginBottom: '6px',
                    userSelect: 'none'
                  }}
                >
                  {freeWriteDays[index] ? '\u2190 Pick from templates' : '\u270F Type instead'}
                </div>

                {freeWriteDays[index] ? (
                  <textarea
                    value={athleteSchedule.workouts?.[day.toLowerCase()] || ''}
                    onChange={(e) => {
                      const updatedSchedule = {
                        ...athleteSchedule,
                        workouts: {
                          ...athleteSchedule.workouts,
                          [day.toLowerCase()]: e.target.value || null
                        }
                      };
                      setAthleteSchedule(updatedSchedule);
                    }}
                    placeholder="Type the workout for this day..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      color: '#1e293b',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                               ) : (
                <>
                {/* Workout Assignment Dropdown */}
                <select
                  value={athleteSchedule.workouts?.[day.toLowerCase()] || ''}
                        onChange={(e) => {
                          const updatedSchedule = {
                            ...athleteSchedule,
                            workouts: {
                              ...athleteSchedule.workouts,
                              [day.toLowerCase()]: e.target.value || null
                            }
                          };
                          setAthleteSchedule(updatedSchedule);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '8px',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          color: '#1e293b',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">No workout</option>
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                </>
                )}
              </div>
            );
          })}
              </div>

              <button
                onClick={handleSaveSchedule}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                💾 Save Schedule
              </button>
            </div>
          </div>
        )}

        {/* Template Creation/Edit Modal */}
        {isEditing && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px'
          }} onClick={handleCancel}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }} onClick={(e) => e.stopPropagation()}>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '32px'
              }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: 0
                }}>
                  {editingTemplate ? 'Edit Template' : 'Create Template'}
                </h2>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: '8px',
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Intensity Level
                </label>
                <select
                  value={formData.intensity}
                  onChange={(e) => setFormData({...formData, intensity: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {intensityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Template Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Base Aerobic, VO2 Max, Recovery Run"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Workout Description
                </label>
                <textarea
                  value={formData.prescription}
                  onChange={(e) => setFormData({...formData, prescription: e.target.value})}
                  placeholder="Describe the workout in detail..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleCancel}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    color: '#64748b',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.borderColor = '#cbd5e1';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f8fafc';
                    e.target.style.borderColor = '#e2e8f0';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 20px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {editingTemplate ? '✅ Update' : '✅ Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Exercise Creation/Edit Modal */}
        {(isAddingExercise || editingExercise) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px'
          }} onClick={cancelExerciseEdit}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }} onClick={(e) => e.stopPropagation()}>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '32px'
              }}>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: 0
                }}>
                  {editingExercise ? 'Edit Exercise' : 'Add Exercise'}
                </h2>
                <button
                  onClick={cancelExerciseEdit}
                  style={{
                    padding: '8px',
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Exercise Name
                </label>
                <input
                  type="text"
                  value={exerciseForm.name}
                  onChange={(e) => setExerciseForm({...exerciseForm, name: e.target.value})}
                  placeholder="e.g., Push-ups, Squats, Burpees"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Category
                </label>
                <select
                  value={exerciseForm.category}
                  onChange={(e) => setExerciseForm({...exerciseForm, category: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.replace('-', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Description
                </label>
                <textarea
                  value={exerciseForm.description}
                  onChange={(e) => setExerciseForm({...exerciseForm, description: e.target.value})}
                  placeholder="How to perform this exercise..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontSize: '16px',
                    fontWeight: '500',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={cancelExerciseEdit}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    color: '#64748b',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={editingExercise ? handleUpdateExercise : handleCreateExercise}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {editingExercise ? '✅ Update' : '✅ Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Exercise Viewer Modal */}
        {viewingExercise && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px'
          }} onClick={() => setViewingExercise(null)}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }} onClick={(e) => e.stopPropagation()}>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBottom: '24px'
              }}>
                <div>
                  <h2 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: '0 0 8px 0'
                  }}>
                    {viewingExercise.name}
                  </h2>
                  <div style={{
                    padding: '4px 8px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    display: 'inline-block'
                  }}>
                    {viewingExercise.category}
                  </div>
                </div>
                <button
                  onClick={() => setViewingExercise(null)}
                  style={{
                    padding: '8px',
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {viewingExercise.description && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Description
                  </h3>
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {viewingExercise.description}
                  </p>
                </div>
              )}

              {viewingExercise.tips && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Tips
                  </h3>
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    margin: 0,
                    fontStyle: 'italic'
                  }}>
                    {viewingExercise.tips}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Onboarding Modals */}
      {showWelcome && (
        <WelcomeModal 
          user={user}
          onClose={handleWelcomeClose}
          onStartTour={handleStartTour}
        />
      )}

      {showTutorial && (
        <InteractiveTutorial
          user={user}
          onComplete={handleTutorialComplete}
        />
      )}
    </div>
  );
};

export default CoachDashboard;